/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Tab, Tabs } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { MdIvesCondition, MdIvesPrjInfo, TabMenu } from 'models/md/MdIves';
import { useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import IvesAtc from './atcMgt/IvesAtc';
import DtlIsptMatr from './atcMgt/DtlIsptMatr';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { downloadExcelTemplatesPost } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';

/**
 * 형개발 유사실적 관리 페이지
 */
const MdSmlPfrsltMgtInqPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initCondition = {
    wbs: '',
  };

  const gridRef = useRef();
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [elmPrjStatCd, setElmPrjStatCd] = useState<Code[]>([]);
  const [condition, setCondition] = useState<any>({ ...initCondition });
  const [rowData, setRowData] = useState<any>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.No', 'No')),
        width: 80,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.관리번호', '관리번호')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.WBS', 'WBS')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.투자명', '투자명')),
        width: 200,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.품의 일자', '품의 일자')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.모델', '모델')),
        width: 90,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.조성', '조성')),
        width: 90,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.공정', '공정')),
        width: 90,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.설비', '설비')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.호기', '호기')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.투자항목', '투자항목')),
        width: 120,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.상세', '상세')),
        width: 240,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.단가', '단가')),
        width: 100,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.수량/단가 기준', '수량/단가 기준')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.비고', '비고')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.수정자', '수정자')),
        width: 100,
        align: 'center',
        dataType: 'Number',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.수정일시', '수정일시')),
        width: 140,
        align: 'center',
        dataType: 'Number',
      },
    ];
  }, [rowData]);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    const elmPrjStatCd = await getCommonCodeNames('MD_ELM_PRJ_STAT_CD');
    setBildPlntCode(bildPlntCode);
    setElmPrjStatCd(elmPrjStatCd);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    // search
  };

  const btnRegist = () => {
    //
  };

  const btnModify = () => {
    //
  };

  const btnDelete = () => {
    //
  };

  const btnTemplateDownload = async () => {
    openLoading(true);
    downloadExcelTemplatesPost(FileTypeName.TPL_SETUP_PKG_LIST, '', [], '').finally(() =>
      openLoading(false)
    );
  };

  const handleDownloadExcel = () => {
    // excel download
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('setup.label.WBS', 'WBS'))}</label>
                <CustomInputWithSearch
                  name="wbs"
                  value={condition.wbs}
                  onChange={(e) => handleOnChange('wbs', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.투자명', '투자명'))}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.모델', '모델')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.조성', '조성')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('setup.label.공정', '공정')}</label>
                <CustomInputWithSearch
                  name="custNm"
                  value={condition.custNm}
                  onChange={(e) => handleOnChange('custNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.설비', '설비')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.투자항목', '투자항목')}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.투자상세', '투자상세')}</label>
                <CustomInputWithSearch
                  name="dataInsUserId"
                  value={condition.dataInsUserId}
                  onSearchClick={(e) => console.log(e)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.형개발 유사실적 목록', '형개발 유사실적 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnTemplateDownload}
            disableRipple
          >
            {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid layoutDefinition={layoutDefinition} rowData={rowData} height={500} />
      <GlobalBtnGroup>
        <Button
          css={IconButton.button}
          className="icoNone ok"
          onClick={() => btnDelete()}
          disableRipple
        >
          {t('com.button.삭제', '삭제')}
        </Button>
        <Button
          css={IconButton.button}
          className="icoNone ok"
          onClick={() => btnModify()}
          disableRipple
        >
          {t('com.button.수정', '수정')}
        </Button>
        <Button
          css={IconButton.button}
          className="icoNone ok"
          onClick={() => btnRegist()}
          disableRipple
        >
          {t('com.button.신규', '신규')}
        </Button>
      </GlobalBtnGroup>
    </>
  );
};

export default MdSmlPfrsltMgtInqPage;
